import { ChevronDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

const categories = [
  { id: "user", label: "KahaApp Users" },
  { id: "business", label: "Business Owners" },
  { id: "collaborator", label: "Companies and Collaborators" },
];

const faqs = {

   user : [
    {
      faqNumber: "1",
      question: "What is KahaApp, and how does it work?",
      answer:
        "KahaApp is a versatile platform that combines navigation, business listings, and digital tools to make exploring, connecting, and managing tasks easier. It provides features like KahaTAG for digital addresses, verified business profiles, in-app communication, and a digital marketplace.",
    },
    {
      faqNumber: "2",
      question: "How do I create an account on KahaApp?",
      answer: (
        <>
          To create an account: 
          <br />
          1. Download KahaApp from the App Store or Google Play Store.
          <br />
          2. Open the app, click 'Sign Up'.
          <br />
          3. Enter your phone number, verify it through the OTP, and set a password.
        </>
      ),
    },
    {
      faqNumber: "3",
      question: "What is KahaTAG, and how do I use it?",
      answer:
        "KahaTAG is a unique digital address assigned to a location or business. Use it to navigate, share, or search for places easily by entering the KahaTAG in the app.",
    },
    {
      faqNumber: "4",
      question: "How do I search for businesses or locations?",
      answer:
        "You can use the search bar in KahaApp to find businesses, landmarks, or services by name, category, or KahaTAG. Results will show verified profiles and navigation options.",
    },
    {
      faqNumber: "5",
      question: "How does the in-app navigation work?",
      answer:
        "The in-app navigation provides step-by-step directions to your destination without switching to external apps. Simply search for a place, select a business, and tap 'Navigate'.",
    },
    {
      faqNumber: "6",
      question: "Can I communicate with businesses directly through the app?",
      answer:
        "Yes, KahaApp offers in-app chat and call features. Visit the business profile and tap on the chat or call icon to get in touch directly.",
    },
    {
      faqNumber: "7",
      question: "How do I know the businesses listed on KahaApp are trustworthy?",
      answer:
        "KahaApp verifies all listed businesses and service providers to ensure they are authentic and reliable. Look for the 'Verified' badge on profiles for added assurance.",
    },
    {
      faqNumber: "8",
      question: "What is the 360° Photos & Videos feature?",
      answer:
        "This feature allows you to virtually explore businesses, accommodations, or landmarks through interactive media. Just tap on the 360° view option on the profile.",
    },
    {
      faqNumber: "9",
      question: "How do I leave a review or rating for a business?",
      answer:
        "After visiting a business, go to its profile on KahaApp and tap on the 'Leave a Review' option to share feedback and rate your experience.",
    },
    {
      faqNumber: "10",
      question: "What is the Digital Marketplace, and how do I use it?",
      answer:
        "The Digital Marketplace is a platform within KahaApp where businesses showcase their products and services. Browse, select items, and follow the prompts to purchase or book them.",
    },
    {
      faqNumber: "11",
      question: "Can I advertise my business on KahaApp?",
      answer: (
        <>
          KahaApp offers multiple advertising options, including:
          <br />
          • Digital Ad Spaces: Display banners to attract attention.
          <br />
          • Push Notifications: Send instant updates or offers to targeted users.
          <br />
          • Notice Boards: Highlight key announcements or promotions.
          <br />
          <br />
          Visit the "Advertise with Us" section in the app to get started.
        </>
      ),
    },
    {
      faqNumber: "12",
      question: "How do I enable push notifications?",
      answer:
        "Push notifications are enabled by default for premium subscribers. Manage notifications in the app settings to receive updates, offers, and alerts.",
    },
    {
      faqNumber: "13",
      question: "What should I do if I face issues while using the app?",
      answer:
        "If you encounter any issues, go to the 'Help & Support' section in the app or contact our customer support team via email or in-app chat.",
    },
    {
      faqNumber: "14",
      question: "Is KahaApp free to use?",
      answer:
        "Yes, KahaApp is free to download and use for basic features. Some premium services, like advertising or advanced tools, may incur charges.",
    },
    {
      faqNumber: "15",
      question: "How do I explore cultural souvenirs on KahaApp?",
      answer:
        "Visit the 'Souvenirs' section in the app to browse our exclusive heritage-inspired collection, view details, and purchase directly from listed retailers.",
    },
  ],

  business : [
    {
      faqNumber: "1",
      question: "What is KahaApp, and how can it benefit my business?",
      answer:
        "KahaApp is a versatile digital platform that connects businesses with customers by providing tools like verified profiling, in-app communication, navigation, and advertising. It enhances your business visibility, builds trust with customers, and simplifies operations.",
    },
    {
      faqNumber: "2",
      question: "How do I register my business on KahaApp?",
      answer: (
        <>
          To register your business:
          <br />
          1. Download KahaApp from the App Store or Google Play Store.
          <br />
          2. Open the app and click on the "Sign Up" button.
          <br />
          3. Enter your phone number.
          <br />
          4. Verify your account through the OTP sent to your phone and set a password.
          <br />
          5. Click on “Profile” Sign up as a business user and fill out your profile details, including your business name, category, location, and services.
          <br />
          6. Submit your information for verification. Once approved, your business will be live on the platform.
        </>
      ),
    },
    {
      faqNumber: "3",
      question: "What is a KahaTAG, and why does my business need it?",
      answer:
        "KahaTAG is a unique digital address that helps customers easily find and navigate to your business. It eliminates confusion caused by traditional addresses and improves customer convenience.",
    },
    {
      faqNumber: "4",
      question: "How does KahaApp verify my business?",
      answer:
        "KahaApp requires you to submit valid documentation (e.g., business registration or ID) to confirm your authenticity. Once verified, your profile gets a 'Verified' badge, which boosts customer trust.",
    },
    {
      faqNumber: "5",
      question: "How can I attract more customers using KahaApp?",
      answer:
        "KahaApp offers features like digital ad spaces, push notifications, and a digital marketplace where you can promote your products or services directly to app users. Customers can also find your business through search, navigation, and recommendations.",
    },
    {
      faqNumber: "6",
      question: "What is the 360° Photos & Videos feature, and how can it help my business?",
      answer:
        "This feature allows you to showcase your business space, products, or services with immersive media. It gives potential customers a virtual experience of your offerings, encouraging them to visit or book with confidence.",
    },
    {
      faqNumber: "7",
      question: "Can customers contact my business directly through KahaApp?",
      answer:
        "Yes, customers can use the in-app chat and call features to contact your business directly for inquiries, bookings, or support, making communication seamless and efficient.",
    },
    {
      faqNumber: "8",
      question: "How can I advertise my business on KahaApp?",
      answer: (
        <>
          KahaApp offers multiple advertising options, including:
          <br />
          • Digital Ad Spaces: Display banners to attract attention.
          <br />
          • Push Notifications: Send instant updates or offers to targeted users.
          <br />
          • Notice Boards: Highlight key announcements or promotions.
          <br />
          <br />
          Visit the "Advertise with Us" section in the app to get started.
        </>
      ),
    },
    {
      faqNumber: "9",
      question: "How does the review and rating system work for businesses?",
      answer:
        "Customers can leave reviews and rate their experiences with your business. Positive reviews and high ratings enhance your credibility, attract more customers, and provide valuable feedback for improvement.",
    },
    {
      faqNumber: "10",
      question: "What is the Digital Marketplace, and how does it work?",
      answer:
        "The Digital Marketplace allows you to list and sell products or services directly on KahaApp. Customers can browse, view prices, and make purchases or bookings, boosting your sales potential.",
    },
    {
      faqNumber: "11",
      question: "How do I notify customers about new offers or services?",
      answer:
        "You can use KahaApp’s push notification feature to send updates, special offers, or announcements directly to customers who follow or interact with your business.",
    },
    {
      faqNumber: "12",
      question: "Is there a cost to register my business on KahaApp?",
      answer:
        "Basic registration is free. However, premium features like advertising, advanced analytics, and additional tools may come with a nominal annual fee.",
    },
    {
      faqNumber: "13",
      question: "Can I update my business profile after registration?",
      answer:
        "Yes, you can update your profile anytime to reflect changes in your business information, including services, photos, or contact details.",
    },
    {
      faqNumber: "14",
      question: "How can I showcase my business in the best way possible?",
      answer: (
        <>
          To make your profile stand out:
          <br />
          1. Add high-quality images, including 360° photos and videos.
          <br />
          2. Ensure your profile details are accurate and complete.
          <br />
          3. Use digital ads and push notifications to promote your business actively.
        </>
      ),
    },
    {
      faqNumber: "15",
      question: "How does KahaApp help with location-based marketing?",
      answer:
        "KahaApp’s navigation and tagging system make it easy for nearby customers to discover your business. You can also target users in your area with localized advertising and offers.",
    },
    {
      faqNumber: "16",
      question: "What kind of businesses can register on KahaApp?",
      answer:
        "KahaApp caters to a wide range of businesses, including restaurants, hotels, EV charging stations, souvenir shops, retail stores, service providers, and many more.",
    },
    {
      faqNumber: "17",
      question: "What should I do if I need help managing my business on KahaApp?",
      answer:
        "Visit the 'Help & Support' section in the app, or contact KahaApp’s support team via in-app chat or email for assistance.",
    },
  ],
  collaborator : [
    {
      faqNumber: "1",
      question: "What is KahaApp, and how can it benefit my company?",
      answer:
        "KahaApp is a comprehensive digital platform that connects users with businesses, services, and cultural landmarks. For companies and collaborators, it offers opportunities for brand visibility, customer engagement, and innovative partnerships in navigation, tourism, and digital marketplaces.",
    },
    {
      faqNumber: "2",
      question: "How can my company collaborate with KahaApp?",
      answer: (
        <>
          Your company can collaborate by:
          <br />
          1. Listing your services or locations on the app.
          <br />
          2. Partnering on joint campaigns or events.
          <br />
          3. Using KahaApp’s digital ad spaces for promotions.
          <br />
          4. Contributing to new features or initiatives that align with your goals.
        </>
      ),
    },
    {
      faqNumber: "3",
      question: "What are the benefits of collaboration with KahaApp?",
      answer:
        "Collaboration with KahaApp provides enhanced brand visibility through targeted advertising and notifications, access to a growing user base of travelers, businesses, and residents, opportunities for co-branding and joint marketing campaigns, and insights into customer behavior and preferences via the app’s analytics.",
    },
    {
      faqNumber: "4",
      question: "Can my company use KahaApp for advertising?",
      answer: (
        <>
          Yes, KahaApp offers a variety of advertising options:
          <br />
          1. Digital Ad Spaces: Promote your brand with banners visible to users.
          <br />
          2. Push Notifications: Send direct updates and offers to your target audience.
          <br />
          3. Notice Boards: Announce collaborations, events, or key initiatives.
        </>
      ),
    },
    {
      faqNumber: "5",
      question: "How does KahaApp’s navigation system help companies?",
      answer:
        "KahaApp’s navigation feature allows users to locate your business or facilities with pinpoint accuracy. This is especially valuable for companies with physical locations, such as hotels, restaurants, or event spaces.",
    },
    {
      faqNumber: "6",
      question: "What is KahaTAG, and how can it be utilized for my company?",
      answer:
        "KahaTAG is a unique digital address system that simplifies location sharing and navigation. Companies can use KahaTAG to provide precise directions to their branches, stores, or offices, simplify logistics and operations for staff and visitors, and offer an enhanced customer experience with accurate navigation.",
    },
    {
      faqNumber: "7",
      question: "How does KahaApp support event partnerships?",
      answer:
        "KahaApp can partner with your company for events by promoting the event through its app and social media platforms, providing digital tools like QR codes, check-in systems, and location tagging, and offering in-app navigation to guide attendees to the venue.",
    },
    {
      faqNumber: "8",
      question: "Can KahaApp integrate with my company’s existing systems or services?",
      answer:
        "Yes, KahaApp can collaborate to explore integration opportunities, such as API connections, data sharing, or custom features that complement your company’s systems and services.",
    },
    {
      faqNumber: "9",
      question: "What type of analytics does KahaApp provide to collaborators?",
      answer:
        "KahaApp offers insights on user engagement with your listings or advertisements, navigation trends and traffic to your locations, and customer feedback through reviews and ratings. These analytics can help refine marketing strategies and operations.",
    },
    {
      faqNumber: "10",
      question: "Can KahaApp help promote my company’s CSR or sustainability projects?",
      answer:
        "Yes, KahaApp can amplify your CSR or sustainability initiatives through digital campaigns highlighting your projects, featuring your efforts in the app’s 'Notice Board' section, and partnering on shared objectives, such as heritage conservation or green energy promotion.",
    },
    {
      faqNumber: "11",
      question: "What industries can collaborate with KahaApp?",
      answer:
        "KahaApp collaborates with a wide range of industries, including tourism, hospitality, retail, cultural heritage, transportation, real estate, and more. If your company aligns with our mission, we’re open to exploring innovative partnerships.",
    },
    {
      faqNumber: "12",
      question: "What are the terms and conditions for collaborating with KahaApp?",
      answer:
        "Collaboration terms vary depending on the project scope and mutual goals. Reach out to the KahaApp team to discuss specific requirements, benefits, and agreements tailored to your company.",
    },
    {
      faqNumber: "13",
      question: "Can my company co-develop features with KahaApp?",
      answer:
        "Yes, KahaApp is open to co-developing features that align with its vision. Collaborators can work on tools like specialized business modules, APIs, or industry-specific solutions.",
    },
    {
      faqNumber: "14",
      question: "How can my company benefit from KahaApp’s cultural souvenirs?",
      answer:
        "KahaApp’s heritage souvenirs can be integrated into your business as gifts, promotional items, or retail products. Collaborating on souvenir promotion helps showcase Nepal’s culture while enhancing your brand’s cultural relevance.",
    },
    {
      faqNumber: "15",
      question: "How do I initiate a collaboration with KahaApp?",
      answer:
        "To start a collaboration, contact the KahaApp team via their website or email. Provide details about your company, your goals, and potential collaboration ideas. The team will follow up to explore opportunities and finalize terms.",
    },
  ]
};

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  const handleClick = () => {
    onToggle();
  };

  useEffect(() => {
    if (isOpen) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  return (
    <div className="mb-4 sm:mb-6 w-full p-4 sm:p-6 rounded-lg bg-[#F8FCFA] shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
      <button
        onClick={handleClick}
        className="w-full flex justify-between items-center text-left"
      >
        <span className={`font-semibold text-base sm:text-lg pr-4 sm:pr-8 ${isOpen ? 'text-[#07A64F]' : 'text-gray-800'}`}>
          {question}
        </span>
        <ChevronDown
          className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          height: `${height}px`,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? "visible" : "hidden",
        }}
      >
        <div className="pt-3 sm:pt-4 text-sm sm:text-base text-gray-600">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const [activeCategory, setActiveCategory] = useState("user");
  const [openFaqs, setOpenFaqs] = useState(null);

  const handleFaqToggle = (faqId) => {
    setOpenFaqs((prev) => (prev === faqId ? null : faqId));
  };

  return (
    <div className="bg-[#fff] py-12 sm:py-16 pt-20 sm:pt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h1 id="faq-title" className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-black mb-8 sm:mb-12 scroll-mt-40">
          Frequently Asked Questions
        </h1>

        {/* Category Buttons */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-6 sm:mb-8">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-colors shadow-md ${
                activeCategory === category.id
                  ? "bg-[#16A951] text-[#fff]"
                  : "bg-[#fff] text-black hover:bg-[#16A951] "
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        <div className="bg-[#fff] rounded-lg shadow-lg p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            <div>
              {faqs[activeCategory]
                .slice(0, Math.ceil(faqs[activeCategory].length / 2))
                .map((faq) => (
                  <FAQItem
                    key={faq.faqNumber}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFaqs === faq.faqNumber}
                    onToggle={() => handleFaqToggle(faq.faqNumber)}
                  />
                ))}
            </div>
            
            <div>
              {faqs[activeCategory]
                .slice(Math.ceil(faqs[activeCategory].length / 2))
                .map((faq) => (
                  <FAQItem
                    key={faq.faqNumber}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFaqs === faq.faqNumber}
                    onToggle={() => handleFaqToggle(faq.faqNumber)}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
